import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import { Team, Subscription } from '../../types';

interface ManageTeamsDialogProps {
  open: boolean;
  onClose: () => void;
  subscription: Subscription | null;
  subscriptions: Subscription[];
  teams: Team[];
  onAssign: (teamId: string) => Promise<{ success: boolean; error?: any }>;
}

const ManageTeamsDialog: React.FC<ManageTeamsDialogProps> = ({
  open,
  onClose,
  subscription,
  subscriptions,
  teams,
  onAssign
}) => {
  // Calculate assigned team IDs by checking which teams have this subscription assigned
  // Using useMemo to avoid recalculating on every render
  const assignedTeamIds = React.useMemo(() => {
    if (!subscription || !teams || teams.length === 0) {
      return new Set<string>();
    }

    const subscriptionId = subscription.id || subscription._id;

    // Find teams that have this subscription ID in their subscriptionId field
    const assignedTeams = teams.filter(team => {
      const teamSubId = team.subscriptionId;
      return teamSubId === subscriptionId;
    });

    const ids = assignedTeams.map(team => team.id || team._id);
    return new Set<string>(ids);
  }, [subscription, teams]);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Helper function to get subscription name by ID
  const getSubscriptionName = (subscriptionId: string): string => {
    const sub = subscriptions.find(s => (s.id || s._id) === subscriptionId);
    return sub ? sub.name : 'Unknown Subscription';
  };

  const handleToggleTeam = async (teamId: string) => {
    const isCurrentlyAssigned = assignedTeamIds.has(teamId);

    // Prevent unchecking - teams cannot be unassigned, only reassigned
    if (isCurrentlyAssigned) {
      setError('Teams cannot be removed from a subscription. To move this team, assign it to a different subscription from that subscription\'s "Manage Teams" dialog.');
      return;
    }

    // Prevent assignment if subscription is inactive
    if (subscription?.availability === 'inactive') {
      setError('Cannot assign teams to an inactive subscription. The subscription has expired or is unavailable.');
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      const result = await onAssign(teamId);

      if (!result.success) {
        setError(result.error?.message || 'Failed to assign team to subscription');
      }
      // Note: We don't update local state here because the parent component
      // (SubscriptionManagement) will refresh the organization data, which will
      // update the subscription.teams array, and our useMemo will recalculate
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  if (!subscription) return null;

  // Check if subscription is inactive
  const isInactive = subscription?.availability === 'inactive';

  // Check limits
  const currentTeamCount = assignedTeamIds.size;
  const maxTeams = (subscription?.limits?.maxTeams || subscription?.limits?.teamsPerOrg || 10) + (subscription?.addOns?.extraTeams || 0);
  const hasReachedLimit = currentTeamCount >= maxTeams;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Manage Teams for {subscription.name}
      </DialogTitle>

      <DialogContent>
        {isInactive && (
          <Alert severity="error" sx={{ mb: 2 }}>
            This subscription is inactive and cannot be assigned to teams. The subscription has expired or is unavailable.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Assign teams to this subscription. Teams assigned: {currentTeamCount} / {maxTeams}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Note: Teams cannot be unassigned, only reassigned to a different subscription.
          </Typography>
        </Box>

        <Divider />

        {(!teams || teams.length === 0) ? (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No teams available. Create a team first.
            </Typography>
          </Box>
        ) : (
          <List>
            {teams.map((team) => {
              const teamId = team.id || team._id;
              const isAssigned = assignedTeamIds.has(teamId);
              const hasOtherSubscription = team.subscriptionId && team.subscriptionId !== (subscription.id || subscription._id);
              const canAssign = (!hasReachedLimit || isAssigned) && !isInactive;

              // Build secondary text - description and subscription assignment on separate lines
              const secondaryContent = (
                <>
                  {team.description && (
                    <Typography component="span" variant="body2" color="text.secondary" display="block">
                      {team.description}
                    </Typography>
                  )}
                  {hasOtherSubscription && team.subscriptionId && (
                    <Typography component="span" variant="body2" color="warning.main" display="block">
                      Currently assigned to: {getSubscriptionName(team.subscriptionId)}
                    </Typography>
                  )}
                </>
              );

              return (
                <ListItem
                  key={teamId}
                  disabled={!canAssign || isLoading || isAssigned}
                  onClick={() => !isAssigned && canAssign && handleToggleTeam(teamId)}
                  sx={{ cursor: !isAssigned && canAssign ? 'pointer' : 'default' }}
                >
                  <ListItemText
                    primary={team.name}
                    secondary={secondaryContent}
                  />
                  <ListItemSecondaryAction>
                    <Checkbox
                      edge="end"
                      checked={isAssigned}
                      onChange={() => !isAssigned && handleToggleTeam(teamId)}
                      disabled={!canAssign || isLoading || isAssigned}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        )}

        {hasReachedLimit && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            You've reached the maximum number of teams for this subscription.
            To assign more teams, upgrade your subscription or purchase additional team add-ons.
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Close
        </Button>
      </DialogActions>

      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 1
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Dialog>
  );
};

export default ManageTeamsDialog;